import { useMemo, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { resolveLogoText, resolveTextSize } from '../render/text';
import type { LogoTemplateProps } from '../types';
import { useLogoTemplateReady } from '../use-logo-template-ready';

const TEXT_SIZING = { maxWidth: 200, baseSize: 40, minSize: 22, charWidth: 0.45 };
const FRAME_HEIGHT = 110;
const BORDER_VIEWBOX_HEIGHT = 171;
const BORDER_INNER_TOP_Y = 3.4;
const BORDER_INNER_BOTTOM_Y = 167.61;
const HORIZONTAL_PADDING = 28;
const MIN_CONTENT_WIDTH = 108;
const VIEWBOX_PADDING = 8;
const VIEWBOX_HEIGHT = 115;
const NARROW_FRAME_REFERENCE_WIDTH = 320;
const TEXT_OPTICAL_NUDGE_Y = 2.5;
const MEASURE_FONT_SIZE = TEXT_SIZING.baseSize;

const BORDER_POINTS =
  '614.58 171.01 351.23 171.01 351.23 167.61 611.19 167.61 611.19 3.4 3.4 3.4 3.4 167.61 261.44 167.61 261.44 171.01 0 171.01 0 0 614.58 0 614.58 171.01';

export function HorizontalBorderedName({
  text,
  palette,
  className,
  style,
  onReady,
}: LogoTemplateProps) {
  const logoText = resolveLogoText(text);
  const layoutKey = `${logoText}|${palette.headerLinks}`;
  const { fontsReady, setSvgRef, svgRef, signalReady } = useLogoTemplateReady({
    onReady,
    layoutKey,
  });

  const [layout, setLayout] = useState(() =>
    layoutFromMeasuredWidth(estimateWidth(logoText, MEASURE_FONT_SIZE), logoText),
  );

  useIsomorphicLayoutEffect(() => {
    if (!onReady) return;
    if (!fontsReady || !svgRef.current) return;
    const measure = svgRef.current.querySelector('[data-logo-measure]') as SVGTextElement | null;
    if (!measure) return;

    const measuredWidth = measure.getBBox().width;
    const next = layoutFromMeasuredWidth(measuredWidth, logoText);
    if (!layoutEqual(layout, next)) {
      setLayout(next);
      return;
    }

    signalReady(svgRef.current);
  }, [onReady, fontsReady, logoText, layout, signalReady, svgRef]);

  const frameInnerCenterY = useMemo(() => {
    const innerMidY = (BORDER_INNER_TOP_Y + BORDER_INNER_BOTTOM_Y) / 2;
    return innerMidY * (FRAME_HEIGHT / BORDER_VIEWBOX_HEIGHT) + TEXT_OPTICAL_NUDGE_Y;
  }, []);

  return (
    <svg
      ref={setSvgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${layout.viewBoxWidth} ${VIEWBOX_HEIGHT}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{ maxHeight: '100%', maxWidth: '100%', display: 'block', ...style }}
      aria-hidden="true"
    >
      <text
        data-logo-measure=""
        fontSize={MEASURE_FONT_SIZE}
        fontFamily="'Barlow Condensed', BarlowCondensed-Regular, Inter, system-ui, sans-serif"
        visibility="hidden"
        x={0}
        y={-1000}
      >
        {logoText}
      </text>
      <g transform={`translate(${VIEWBOX_PADDING / 2}, ${(VIEWBOX_HEIGHT - FRAME_HEIGHT) / 2})`}>
        <svg
          x={0}
          y={0}
          width={layout.frameWidth}
          height={FRAME_HEIGHT}
          viewBox="0 0 615 171"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <polygon points={BORDER_POINTS} fill={palette.headerLinks} />
        </svg>
        <text
          transform={`translate(${layout.frameWidth / 2}, ${frameInnerCenterY})`}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={palette.headerLinks}
          fontFamily="'Barlow Condensed', BarlowCondensed-Regular, Inter, system-ui, sans-serif"
          fontSize={layout.fontSize}
        >
          {logoText}
        </text>
      </g>
    </svg>
  );
}

type Layout = {
  fontSize: number;
  frameWidth: number;
  viewBoxWidth: number;
};

function layoutFromMeasuredWidth(measuredAtBase: number, logoText: string): Layout {
  let fontSize = resolveTextSize(logoText, TEXT_SIZING);
  // Scale measured width from base size to target font size (linear with font-size).
  let textWidth = measuredAtBase * (fontSize / MEASURE_FONT_SIZE);
  let frameWidth = Math.max(textWidth + 2 * HORIZONTAL_PADDING, MIN_CONTENT_WIDTH);

  if (frameWidth < NARROW_FRAME_REFERENCE_WIDTH && fontSize >= TEXT_SIZING.baseSize - 0.01) {
    fontSize = clamp(
      fontSize * (frameWidth / NARROW_FRAME_REFERENCE_WIDTH),
      TEXT_SIZING.minSize,
      TEXT_SIZING.baseSize,
    );
    textWidth = measuredAtBase * (fontSize / MEASURE_FONT_SIZE);
    frameWidth = Math.max(textWidth + 2 * HORIZONTAL_PADDING, MIN_CONTENT_WIDTH);
  }

  return {
    fontSize,
    frameWidth,
    viewBoxWidth: frameWidth + VIEWBOX_PADDING,
  };
}

function estimateWidth(text: string, fontSize: number): number {
  return text.length * fontSize * TEXT_SIZING.charWidth;
}

function layoutEqual(a: Layout, b: Layout): boolean {
  return (
    Math.abs(a.fontSize - b.fontSize) < 0.01 &&
    Math.abs(a.frameWidth - b.frameWidth) < 0.01 &&
    Math.abs(a.viewBoxWidth - b.viewBoxWidth) < 0.01
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
